import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  MessageSquare, Users, Trophy, Crown, Heart, BookOpen, FileText,
  Shield, Mail, MessageCircle, Server, Award, ChevronUp, Sparkles,
  ChevronRight, Globe, Newspaper
} from "lucide-react";
import api from "../lib/api";

const DEFAULT_QUICK = [
  { url: "/stiri", label: "Știri & Patch Notes", icon: Newspaper },
  { url: "/forums", label: "Forumuri", icon: MessageSquare },
  { url: "/members", label: "Membri", icon: Users },
  { url: "/leaderboard", label: "Clasament", icon: Trophy },
  { url: "/vip", label: "Loadout VIP", icon: Crown },
  { url: "/servere", label: "Servere Live", icon: Server },
  { url: "/donate", label: "Donează", icon: Heart },
];

const DEFAULT_COMMUNITY = [
  { url: "/forums", label: "CS2 Competitive", icon: Award },
  { url: "/forums", label: "CS 1.6 Servers & Mods", icon: Server },
  { url: "/clans", label: "Clanuri & Echipe", icon: Shield },
  { url: "/marketplace", label: "VIP Marketplace", icon: Sparkles },
  { url: "/forums", label: "Anunțuri Oficiale", icon: MessageSquare },
];

const DEFAULT_INFO = [
  { url: "/p/rules", label: "Regulament Forum", icon: BookOpen },
  { url: "/p/tos", label: "Termeni & Condiții", icon: FileText },
  { url: "/p/privacy", label: "Politică de Confidențialitate", icon: Shield },
  { url: "/messages", label: "Contact Staff / Admin", icon: Mail },
];

export default function Footer() {
  const [quick] = useState(DEFAULT_QUICK);
  const [info] = useState(DEFAULT_INFO);
  const [social, setSocial] = useState({});
  const [appearance, setAppearance] = useState({});

  useEffect(() => {
    api.get("/idg/settings/public").then((r) => setSocial(r.data || {})).catch(() => {});
    api.get("/idg/appearance").then((r) => setAppearance(r.data || {})).catch(() => {});
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <footer className="mt-20 border-t border-[#141824] bg-[#06080d] relative text-zinc-300 font-sans shadow-2xl" data-testid="site-footer">
      
      {/* Decorative top glow line */}
      <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-[var(--tf-primary)]/70 to-transparent" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 lg:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-10">
          
          {/* 1. BRAND & IDENTITY (4 Cols) */}
          <div className="lg:col-span-4 space-y-4">
            <div className="flex items-center gap-3.5">
              {appearance?.logo_dark_url || appearance?.logo_url ? (
                <img 
                  src={appearance.logo_dark_url || appearance.logo_url} 
                  alt="Logo" 
                  className="w-12 h-12 object-contain shadow-md" 
                />
              ) : (
                <div 
                  className="w-12 h-12 flex items-center justify-center bg-[var(--tf-primary)] text-black font-black font-chakra text-2xl shadow-[0_0_20px_rgba(255,79,0,0.3)] shrink-0"
                >
                  IR
                </div>
              )}
              <div>
                <div className="font-chakra text-white font-black text-lg sm:text-xl tracking-wider uppercase leading-none notranslate" translate="no">
                  {appearance?.header_logo_text ? `${appearance.header_logo_text} ROMANIA` : "INDUNGI ROMANIA"}
                </div>
                <div className="text-[10px] text-amber-400 font-mono tracking-widest mt-1 uppercase font-bold">
                  COMMUNITY NETWORK · EST. 2010
                </div>
              </div>
            </div>

            <p className="text-xs text-zinc-400 font-mono leading-relaxed max-w-sm">
              Cea mai activă comunitate românească de Counter-Strike competitiv, mix-uri 5v5 și servere publice. Dezvoltat de gameri, pentru gameri.
            </p>

            {/* Social Media Glowing Buttons */}
            <div className="flex items-center gap-2 flex-wrap pt-2">
              <SocialBtn href={social.social_discord || "https://discord.gg/CrGC77X8W"} icon={MessageCircle} label="Discord" hoverColor="#5865F2" />
              <SocialBtn href={social.social_steam || "https://steamcommunity.com/groups/indungi"} icon={Globe} label="Steam" hoverColor="#00F0FF" />
              <SocialBtn href={social.social_instagram || "https://instagram.com/indungi.pro"} icon={InstagramIcon} label="Instagram" hoverColor="#E1306C" />
              <SocialBtn href={social.social_tiktok || "https://tiktok.com/@indungi.pro"} icon={TikTokIcon} label="TikTok" hoverColor="#00F2FE" />
              <SocialBtn href={social.social_youtube || "https://youtube.com"} icon={YoutubeIcon} label="YouTube" hoverColor="#FF0000" />
              <SocialBtn href={`mailto:${social.smtp_from_email || "contact@indungi.ro"}`} icon={Mail} label="Email Contact" hoverColor="var(--tf-primary)" />
            </div>
          </div>

          {/* 2. QUICK LINKS (3 Cols) */}
          <div className="lg:col-span-3 space-y-4" data-testid="footer-quick">
            <h4 className="text-xs font-chakra font-black uppercase tracking-wider text-amber-400 flex items-center gap-2">
              <span className="w-1.5 h-1.5 bg-amber-400 shadow-[0_0_6px_#f59e0b]" />
              <span>NAVIGAȚIE RAPIDĂ</span>
            </h4>
            <ul className="space-y-1.5">
              {quick.map((q) => {
                const Icon = q.icon || MessageSquare;
                return (
                  <li key={q.url}>
                    <Link 
                      to={q.url} 
                      className="flex items-center gap-2 text-xs font-mono text-zinc-400 hover:text-white hover:translate-x-1.5 transition-all py-1 group"
                    >
                      <ChevronRight className="w-3 h-3 text-zinc-600 group-hover:text-amber-400 transition-colors shrink-0" />
                      <Icon className="w-3.5 h-3.5 text-zinc-500 group-hover:text-amber-400 transition-colors shrink-0" />
                      <span className="group-hover:text-white transition-colors">{q.label}</span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>

          {/* 3. COMMUNITY & GAMING (2 Cols) */}
          <div className="lg:col-span-2 space-y-4">
            <h4 className="text-xs font-chakra font-black uppercase tracking-wider text-cyan-400 flex items-center gap-2">
              <span className="w-1.5 h-1.5 bg-cyan-400 shadow-[0_0_6px_#06b6d4]" />
              <span>COMUNITATE</span>
            </h4>
            <ul className="space-y-1.5">
              {DEFAULT_COMMUNITY.map((c, i) => {
                const Icon = c.icon;
                return (
                  <li key={i}>
                    <Link 
                      to={c.url} 
                      className="flex items-center gap-2 text-xs font-mono text-zinc-400 hover:text-white hover:translate-x-1.5 transition-all py-1 group"
                    >
                      <ChevronRight className="w-3 h-3 text-zinc-600 group-hover:text-cyan-400 transition-colors shrink-0" />
                      <Icon className="w-3.5 h-3.5 text-zinc-500 group-hover:text-cyan-400 transition-colors shrink-0" />
                      <span className="group-hover:text-white transition-colors">{c.label}</span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>

          {/* 4. INFO & SUPPORT (3 Cols) */}
          <div className="lg:col-span-3 space-y-4" data-testid="footer-info">
            <h4 className="text-xs font-chakra font-black uppercase tracking-wider text-emerald-400 flex items-center gap-2">
              <span className="w-1.5 h-1.5 bg-emerald-400 shadow-[0_0_6px_#10b981]" />
              <span>INFORMAȚII & SUPORT</span>
            </h4>
            <ul className="space-y-1.5">
              {info.map((inf, i) => {
                const Icon = inf.icon || FileText;
                return (
                  <li key={i}>
                    <Link 
                      to={inf.url} 
                      className="flex items-center gap-2 text-xs font-mono text-zinc-400 hover:text-white hover:translate-x-1.5 transition-all py-1 group"
                    >
                      <ChevronRight className="w-3 h-3 text-zinc-600 group-hover:text-emerald-400 transition-colors shrink-0" />
                      <Icon className="w-3.5 h-3.5 text-zinc-500 group-hover:text-emerald-400 transition-colors shrink-0" />
                      <span className="group-hover:text-white transition-colors">{inf.label}</span>
                    </Link>
                  </li>
                );
              })}
              <li className="pt-2">
                <div className="px-3 py-2 bg-[#090c12] border border-emerald-500/30 text-[11px] text-emerald-400 flex items-center gap-2 font-mono shadow-[0_0_15px_rgba(16,185,129,0.08)]">
                  <Shield className="w-4 h-4 text-emerald-400 shrink-0" />
                  <span className="font-bold">Comunitate securizată din 2010</span>
                </div>
              </li>
            </ul>
          </div>

        </div>
      </div>

      {/* BOTTOM BAR: COPYRIGHT & BACK TO TOP */}
      <div className="border-t border-[#121620] bg-black/70 py-4 px-4 sm:px-6 text-xs text-zinc-500 font-mono">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="text-center sm:text-left">
            © {new Date().getFullYear()} <strong className="text-zinc-300 font-chakra font-black tracking-wider uppercase">INDUNGI ROMÂNIA</strong> · Toate drepturile rezervate.
          </div>

          <div className="flex items-center gap-4">
            <span className="flex items-center gap-2 text-[11px] font-mono">
              <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse shadow-[0_0_8px_#34d399]" />
              <span className="text-zinc-400">Sisteme Operaționale</span>
            </span>
            <button
              onClick={scrollToTop}
              className="bg-[#090c12] hover:bg-[#0e131d] border border-[#181e2b] hover:border-[var(--tf-primary)] text-zinc-300 hover:text-white text-[10px] px-3.5 py-1.5 flex items-center gap-1.5 transition-all font-chakra font-bold uppercase tracking-wider shadow-sm cursor-pointer"
              title="Înapoi sus"
            >
              <ChevronUp className="w-3.5 h-3.5 text-[var(--tf-primary)]" />
              <span>SUS</span>
            </button>
          </div>
        </div>
      </div>

    </footer>
  );
}

function SocialBtn({ href, icon: Icon, label, hoverColor }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      title={label}
      className="w-9 h-9 flex items-center justify-center bg-[#090c12] border border-[#141824] hover:border-[var(--tf-primary)] text-zinc-400 hover:text-white hover:scale-110 transition-all shadow-md group"
      style={{
        "--hover-color": hoverColor || "var(--tf-primary)"
      }}
    >
      <Icon className="w-4 h-4 transition-colors group-hover:text-[var(--hover-color)]" />
    </a>
  );
}

function TikTokIcon({ className }) {
  return (
    <svg className={className || "w-4 h-4"} viewBox="0 0 24 24" fill="currentColor">
      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.93a8.16 8.16 0 0 0 4.7 1.43V6.93l-1.77-.24z" />
    </svg>
  );
}

function InstagramIcon({ className }) {
  return (
    <svg className={className || "w-4 h-4"} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect width="20" height="20" x="2" y="2" rx="5" ry="5"/>
      <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/>
      <line x1="17.5" x2="17.51" y1="6.5" y2="6.5"/>
    </svg>
  );
}

function YoutubeIcon({ className }) {
  return (
    <svg className={className || "w-4 h-4"} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2.5 17a24.12 24.12 0 0 1 0-10 2 2 0 0 1 1.4-1.4 49.56 49.56 0 0 1 16.2 0A2 2 0 0 1 21.5 7a24.12 24.12 0 0 1 0 10 2 2 0 0 1-1.4 1.4 49.55 49.55 0 0 1-16.2 0A2 2 0 0 1 2.5 17"/>
      <polygon points="10 15 15 12 10 9 10 15"/>
    </svg>
  );
}
